import { useState } from 'react';
import { Copy, Download, ExternalLink, CheckCircle2, FileText, BookOpen, Plug, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import type { SkillAgent } from '../../../../shared/types';
import { useTranslation } from 'react-i18next';

type TFunction = ReturnType<typeof useTranslation>['t'];

interface AgentSetupModalProps {
  skill: SkillAgent;
  open: boolean;
  onClose: () => void;
  onUseCount?: () => void;
}

// Función de `t`: los textos salen de i18n y `t` no existe a nivel de módulo.
const STEPS = (t: TFunction) => ([
  {
    icon: <Download className="h-4 w-4" />,
    title: t('skills.agentsetupmodal.descarga_el_paquete_completo'),
    desc: 'Contiene el prompt, bases de conocimiento y el README con las instrucciones de configuración.',
    action: 'download',
  },
  {
    icon: <ExternalLink className="h-4 w-4" />,
    title: t('skills.agentsetupmodal.crea_un_project_en_claude_ai'),
    desc: 'Andá a claude.ai → Projects → Nuevo proyecto. Dale un nombre al proyecto.',
    action: 'link',
    href: 'https://claude.ai/projects',
  },
  {
    icon: <BookOpen className="h-4 w-4" />,
    title: t('skills.agentsetupmodal.subi_los_archivos_de_contexto'),
    desc: 'Arrastrá los archivos .md del ZIP a la sección "Project Knowledge" del proyecto.',
    action: null,
  },
  {
    icon: <FileText className="h-4 w-4" />,
    title: t('skills.agentsetupmodal.pega_el_system_prompt'),
    desc: 'Copiá el prompt y pegalo en "Project Instructions" (Custom Instructions del proyecto).',
    action: 'copy',
  },
  {
    icon: <Plug className="h-4 w-4" />,
    title: t('skills.agentsetupmodal.configura_las_integraciones'),
    desc: 'Seguí el README para conectar los MCPs necesarios (Slack, HubSpot, Drive, etc.).',
    action: null,
  },
]);

export function AgentSetupModal({ skill, open, onClose, onUseCount }: AgentSetupModalProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const { data: contextZipData } = trpc.skills.getContextDownloadUrl.useQuery(
    { s3Key: skill.contextZipKey ?? '' },
    { enabled: !!skill.contextZipKey && open, staleTime: 50 * 60 * 1000 }
  );

  const currentPrompt = skill.versions.find((v) => v.isCurrent)?.prompt ?? '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentPrompt);
      setCopied(true);
      onUseCount?.();
      toast.success(t('skills.agentsetupmodal.prompt_copiado'));
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error(t('skills.agentsetupmodal.no_se_pudo_copiar'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Bot className="h-4 w-4" />
            </div>
            <DialogTitle className="text-lg">{skill.name}</DialogTitle>
          </div>
          <DialogDescription className="text-sm text-muted-foreground">
            {skill.description}
          </DialogDescription>
        </DialogHeader>

        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-2">
          {t('skills.agentsetupmodal.como_activar_este_agente')}
        </p>

        <ol className="space-y-3 mt-1">
          {STEPS(t).map((step, i) => (
            <li key={i} className="flex gap-3">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground mt-0.5">
                {i + 1}
              </div>
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-1.5 text-sm font-medium">
                  {step.icon}
                  {step.title}
                </div>
                <p className="text-xs text-muted-foreground">{step.desc}</p>
                {step.action === 'download' && contextZipData?.url && (
                  <a
                    href={contextZipData.url}
                    className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline font-medium mt-0.5"
                    onClick={() => onUseCount?.()}
                  >
                    <Download className="h-3.5 w-3.5" />
                    {t('skills.agentsetupmodal.descargar_paquete_completo_zip')}
                  </a>
                )}
                {step.action === 'link' && step.href && (
                  <a
                    href={step.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline font-medium mt-0.5"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {t('skills.agentsetupmodal.ir_a_claude_ai_projects')}
                  </a>
                )}
                {step.action === 'copy' && (
                  <button
                    onClick={handleCopy}
                    className={cn(
                      'inline-flex items-center gap-1.5 text-xs font-medium mt-0.5 transition-colors',
                      copied ? 'text-green-600' : 'text-primary hover:underline'
                    )}
                  >
                    {copied
                      ? <><CheckCircle2 className="h-3.5 w-3.5" /> Copiado</>
                      : <><Copy className="h-3.5 w-3.5" /> {t('skills.agentsetupmodal.copiar_prompt')}</>
                    }
                  </button>
                )}
              </div>
            </li>
          ))}
        </ol>

        <div className="flex gap-2 pt-2 border-t mt-2">
          {contextZipData?.url && (
            <Button asChild className="flex-1" onClick={() => onUseCount?.()}>
              <a href={contextZipData.url}>
                <Download className="h-4 w-4 mr-2" />
                {t('skills.agentsetupmodal.descargar_paquete')}
              </a>
            </Button>
          )}
          <Button
            variant={contextZipData?.url ? 'outline' : 'default'}
            className="flex-1"
            onClick={handleCopy}
          >
            {copied
              ? <><CheckCircle2 className="h-4 w-4 mr-2" /> Copiado</>
              : <><Copy className="h-4 w-4 mr-2" /> {t('skills.agentsetupmodal.copiar_prompt')}</>
            }
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
